import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import usePersistedState from '../../hooks/usePersistedState';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectTripsList } from '../../redux/trip/trip.selectors';
import { fetchTrips } from '../../redux/trip/trip.slice';
import { Trip } from '../../redux/trip/trip.types';
import { isOwner } from '../../redux/trip/trip.utils';
import { selectCurrentUser, selectIsAuthInitialized } from '../../redux/user/user.selectors';
import Spinner from '../Spinner/Spinner';
import CreateTripModal from './components/CreateTripModal/CreateTripModal';
import SortByDropdown from './components/SortByDropdown';
import TripsListItem from './components/TripsListItem';
import './TripsList.scss';

function TripsList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const tripsList = useAppSelector(selectTripsList);

  const [sortOption, setSortOption] = usePersistedState('sortOption', 'Last Opened');
  const [sortedTripsList, setSortedTripsList] = useState<Trip[]>([]);

  const isLoading = !isAuthInitialized || !tripsList;

  useEffect(() => {
    if (isAuthInitialized && !currentUser) {
      navigate('/login');
    }
  }, [isAuthInitialized, currentUser, navigate]);

  const cleanUpTitleInURL = (str: string) => str.replace(/\s+/g, '-').toLowerCase(); // removes spaces and adds hyphens to title in

  useEffect(() => {
    if (tripsList) {
      let sortedList = [...tripsList];

      if (sortOption === 'Alphabetical: A-Z') {
        // Sorting tripsList alphabetically by title
        sortedList = sortedList.sort((a, b) => a.title.localeCompare(b.title));
      } else if (sortOption === 'Last Opened') {
        // Sort trips list by lastOpened timestamps
        sortedList = sortedList.sort((a, b) => {
          const userId = currentUser?.uid;
          if (!userId) {
            return 0;
          }

          const aLastOpened = a.lastOpened?.[userId] ? Date.parse(a.lastOpened[userId]) : 0;
          const bLastOpened = b.lastOpened?.[userId] ? Date.parse(b.lastOpened[userId]) : 0;

          return bLastOpened - aLastOpened;
        });
      }
      setSortedTripsList(sortedList);
    }
  }, [sortOption, tripsList, currentUser]);

  useEffect(() => {
    if (currentUser) {
      dispatch(fetchTrips(currentUser.uid));
    }
  }, [currentUser, dispatch]);

  const tripCountText = tripsList.length === 1 ? '1 Trip' : `${tripsList.length} Trips`;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Helmet>
        <title>Wander | All Trips</title>
      </Helmet>
      <div className='trips-list-header'>Adventures</div>
      <div className='trips-list-info-container'>
        <div className='trip-count'>{tripCountText}</div>
        <div className='trips-list-sort-menu-container'>
          <span className='sort-by-label'>Sort By:</span>
          <SortByDropdown
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </div>
      </div>
      <div className='trips-list-container'>
        {sortedTripsList &&
          sortedTripsList.map((trip) => (
            <TripsListItem
              key={trip.id}
              tripId={trip.id}
              title={trip.title}
              location={trip.place}
              startDateISO={trip?.startDateISO}
              endDateISO={trip?.endDateISO}
              userRoles={trip.roles}
              canDelete={isOwner(trip, currentUser)}
              onClick={() => navigate(`${trip.id}/${cleanUpTitleInURL(trip.title)}`)}
            />
          ))}

        <CreateTripModal />
      </div>
    </>
  );
}

TripsList.displayName = 'TripsList';
export default TripsList;
